import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { ButtonStyleProps } from '../button.type';
import { buttonBase, getButtonSizeClassName, getFocusOutlineClassName, getFocusClassName } from './common.styles';

const SECONDARY = css`
  background: transparent;
  border: 1px solid ${theme.colors.neutral30};
  color: ${theme.font.colors.default};
`;

const SECONDARY_ICON = css`
  background: transparent;
  border: 1px solid transparent;
  color: ${theme.font.colors.default};
`;

const SECONDARY_DESTRUCTIVE = css`
  background: transparent;
  border: 1px solid ${theme.colors.neutral30};
  color: ${theme.colors.status.critical};
`;

const SECONDARY_DESTRUCTIVE_ICON = css`
  background: transparent;
  border: 1px solid transparent;
  color: ${theme.colors.status.critical};
`;

const SECONDARY_DISABLED = css`
  &[aria-disabled='true'] {
    background: transparent;
    border-color: ${theme.colors.neutral30};
    color: ${theme.colors.status.disabled};
  }
`;

const SECONDARY_DISABLED_ICON = css`
  &[aria-disabled='true'] {
    background: transparent;
    border-color: transparent;
    color: ${theme.colors.status.disabled};
  }
`;

const SECONDARY_HOVER = css`
  &:hover:not([aria-disabled='true']) {
    background: ${theme.colors.neutral10};
    border-color: ${theme.colors.neutral30};
  }
`;

const SECONDARY_HOVER_ICON = css`
  &:hover:not([aria-disabled='true']) {
    background: ${theme.colors.neutral10};
    border-color: transparent;
  }
`;

const SECONDARY_DESTRUCTIVE_HOVER = css`
  &:hover:not([aria-disabled='true']) {
    background: ${theme.colors.critical5};
    border-color: ${theme.colors.neutral30};
  }
`;

const SECONDARY_DESTRUCTIVE_HOVER_ICON = css`
  &:hover:not([aria-disabled='true']) {
    background: ${theme.colors.critical5};
    border-color: transparent;
  }
`;

const SECONDARY_PRESSED = css`
  &:active:not([aria-disabled='true']) {
    background: ${theme.colors.neutral10};
    border-color: ${theme.colors.neutral90};
  }
`;

const SECONDARY_PRESSED_ICON = css`
  &:active:not([aria-disabled='true']) {
    background: ${theme.colors.neutral10};
    border-color: transparent;
  }
`;

const SECONDARY_DESTRUCTIVE_PRESSED = css`
  &:active:not([aria-disabled='true']) {
    background: ${theme.colors.critical10};
    color: ${theme.colors.critical70};
    border-color: ${theme.colors.critical70};

    svg {
      color: ${theme.colors.critical70};
    }
  }
`;

const SECONDARY_DESTRUCTIVE_PRESSED_ICON = css`
  &:active:not([aria-disabled='true']) {
    background: ${theme.colors.critical10};
    color: ${theme.colors.critical70};
    border-color: transparent;

    svg {
      color: ${theme.colors.critical70};
    }
  }
`;

const SECONDARY_LOADING = css`
  background: transparent;
  border: 1px solid ${theme.colors.neutral30};

  &[aria-disabled='true'] {
    box-shadow: none;
    color: ${theme.colors.status.disabled};
  }
`;

const SECONDARY_LOADING_ICON = css`
  background: transparent;
  border: 1px solid transparent;

  &[aria-disabled='true'] {
    box-shadow: none;
    color: ${theme.colors.status.disabled};
  }
`;

const getVariantClass = (destructive?: boolean, iconOnly?: boolean) => {
  if (destructive) {
    return iconOnly ? SECONDARY_DESTRUCTIVE_ICON : SECONDARY_DESTRUCTIVE;
  }

  return iconOnly ? SECONDARY_ICON : SECONDARY;
};

const getDisabledClass = (iconOnly: boolean) => (iconOnly ? SECONDARY_DISABLED_ICON : SECONDARY_DISABLED);

const getHoverClass = (destructive?: boolean, iconOnly?: boolean) => {
  if (destructive) {
    return iconOnly ? SECONDARY_DESTRUCTIVE_HOVER_ICON : SECONDARY_DESTRUCTIVE_HOVER;
  }

  return iconOnly ? SECONDARY_HOVER_ICON : SECONDARY_HOVER;
};

const getPressedClass = (destructive?: boolean, iconOnly?: boolean) => {
  if (destructive) {
    return iconOnly ? SECONDARY_DESTRUCTIVE_PRESSED_ICON : SECONDARY_DESTRUCTIVE_PRESSED;
  }

  return iconOnly ? SECONDARY_PRESSED_ICON : SECONDARY_PRESSED;
};

const getLoadingClass = (iconOnly: boolean) => (iconOnly ? SECONDARY_LOADING_ICON : SECONDARY_LOADING);

export const secondaryClassName = ({
  size,
  iconOnly,
  destructive,
  showFocusOutline,
  isLoading,
}: Pick<ButtonStyleProps, 'size' | 'iconOnly' | 'destructive' | 'showFocusOutline' | 'isLoading'>) => {
  const focusVariant = destructive ? 'critical' : 'neutral';

  return cx(
    buttonBase,
    getButtonSizeClassName(size ?? 'small', iconOnly),
    getFocusClassName(focusVariant),
    showFocusOutline ? getFocusOutlineClassName(focusVariant) : null,

    getVariantClass(destructive, iconOnly),
    !isLoading && getDisabledClass(iconOnly),

    isLoading ? getLoadingClass(iconOnly) : getHoverClass(destructive, iconOnly),

    !isLoading && getPressedClass(destructive, iconOnly)
  );
};
